import type { PostFiltersProps, ThemeComponents, ThemeConfig } from '@routeroyal/theme-core';
import type { ComponentType } from 'react';

/**
 * Extended theme components for Stone Investment
 * Includes additional components beyond the core ThemeComponents interface
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StoneInvestmentComponents = ThemeComponents & Record<string, ComponentType<any>>;

import { StoneInvestmentAgentsSection } from './components/AgentsSection';
import { StoneInvestmentContactForm } from './components/ContactForm';
import { StoneInvestmentCTASection } from './components/CTASection';
import { StoneInvestmentFeaturedPropertiesCarousel } from './components/FeaturedPropertiesCarousel';
import { StoneInvestmentFooter } from './components/Footer';
import { StoneInvestmentFooterTeaser } from './components/FooterTeaser';
import { StoneInvestmentHero } from './components/Hero';
import { StoneInvestmentIntroductionSection } from './components/IntroductionSection';
import { StoneInvestmentLocaleSwitcher } from './components/LocaleSwitcher';
import { StoneInvestmentNavigation } from './components/Navigation';
import { StoneInvestmentPageContent } from './components/PageContent';
import { StoneInvestmentPageHeader } from './components/PageHeader';
import { StoneInvestmentPostArticle } from './components/PostArticle';
import { StoneInvestmentPostCard } from './components/PostCard';
import { StoneInvestmentPostFilters } from './components/PostFilters';
import { StoneInvestmentPostGrid } from './components/PostGrid';
import { StoneInvestmentPostHero } from './components/PostHero';
import { StoneInvestmentPostPagination } from './components/PostPagination';
import { StoneInvestmentPostSearch } from './components/PostSearch';
import { StoneInvestmentPropertyCard } from './components/PropertyCard';
import { StoneInvestmentServiceCard } from './components/ServiceCard';
import { StoneInvestmentServicesSection } from './components/ServicesSection';
import { StoneInvestmentSignatureSelection } from './components/SignatureSelection';
import { StoneInvestmentStatsSection } from './components/StatsSection';

// Dynamic Listing Components (API Integration)
import { StoneListingCard } from './components/StoneListingCard';
import { StoneListingGrid } from './components/StoneListingGrid';
import { StoneListingsContent } from './components/StoneListingsContent';

// Filter/Search Components
import { StoneAdvancedFiltersModal } from './components/StoneAdvancedFiltersModal';
import { StoneFilterPanel } from './components/StoneFilterPanel';
import { StonePagination } from './components/StonePagination';
import { StoneSearchBar } from './components/StoneSearchBar';
import { StoneSortControl } from './components/StoneSortControl';
import { StoneViewSwitcher } from './components/StoneViewSwitcher';

// Area Page Components
import { StoneAreaHero } from './components/StoneAreaHero';
import { StoneAreaMap } from './components/StoneAreaMap';
import { StoneAreaOverview } from './components/StoneAreaOverview';
import { StoneAreaPage } from './components/StoneAreaPage';
import { StoneAreaProperties } from './components/StoneAreaProperties';
import { StoneAreaSelector } from './components/StoneAreaSelector';
import { StoneAreaStats } from './components/StoneAreaStats';

// Guide Page Components (exported separately below, not in the components map)

// Project Components
// import { StoneDevelopmentCard } from './components/StoneDevelopmentCard';
// import { StoneDevelopmentGrid } from './components/StoneDevelopmentGrid';
import { StoneProjectCard } from './components/StoneProjectCard';
import { StoneProjectDetailContent } from './components/StoneProjectDetailContent';
import { StoneProjectDetailHero } from './components/StoneProjectDetailHero';
import { StoneProjectHeroCarousel } from './components/StoneProjectHeroCarousel';
import { StoneProjectShowcase } from './components/StoneProjectShowcase';
import { StoneProjectsListView } from './components/StoneProjectsListView';

// About Page Components
import { StoneCompanyStory } from './components/StoneCompanyStory';
import { StoneLocationsGrid } from './components/StoneLocationsGrid';
import { StonePageHero } from './components/StonePageHero';
import { StoneTeamSection } from './components/StoneTeamSection';
import { StoneValuesSection } from './components/StoneValuesSection';

// Contact Components
import { StoneContactFormSection } from './components/StoneContactFormSection';
import { StoneContactMapHero } from './components/StoneContactMapHero';
import { StoneContactSection } from './components/StoneContactSection';
import { StonePhoneInput } from './components/StonePhoneInput';

// Royale Spaces Integration
import { StoneImagineSpaceButton } from './components/StoneImagineSpaceButton';
import { StoneSpacesCTA } from './components/StoneSpacesCTA';
import { StoneSpacesModal } from './components/StoneSpacesModal';
import { StoneSpacesWizard } from './components/StoneSpacesWizard';

// Utility Components
import { StoneComingSoon } from './components/StoneComingSoon';
import { StoneMapComponent } from './components/StoneMapComponent';
import { StonePageTableOfContents } from './components/StonePageTableOfContents';
import { StonePropertyListCard } from './components/StonePropertyListCard';
import { StonePropertyMapView } from './components/StonePropertyMapView';

// Core Property Detail Wrappers
import { StonePropertyOverview } from './components/StonePropertyOverview';
import { StonePropertyGallery } from './components/StonePropertyGallery';
import { StonePropertyFeatures } from './components/StonePropertyFeatures';
import { StonePropertyAmenities } from './components/StonePropertyAmenities';
import { StonePropertyContactCard } from './components/StonePropertyContactCard';
import { StonePropertyLocation } from './components/StonePropertyLocation';

// Contact Information Section
import { StoneContactInformationSection } from './components/StoneContactInformationSection';

// GEO FAQ
import { StoneFAQSection } from './components/StoneFAQSection';

// Legacy Blog Components (ThemeComponents compliance)
import { StoneBlogCard } from './components/StoneBlogCard';
import { StoneBlogHero } from './components/StoneBlogHero';
import { StoneBlogSearch } from './components/StoneBlogSearch';
import { StoneBlogFilters } from './components/StoneBlogFilters';
import { StoneBlogArticle } from './components/StoneBlogArticle';
import { StoneBlogGrid } from './components/StoneBlogGrid';

// Section components are exported directly, not used in theme.components
// Import them via: import { StoneInvestmentMenuOverlay, etc. } from '@routeroyal/theme-stone-investment'

/**
 * Stone Investment Theme Configuration
 * 
 * A sophisticated, luxury portfolio theme for Stone Investment's 
 * high-end real estate platform. Designed to present properties 
 * as exclusive portfolio pieces with:
 * 
 * - Greyscale color palette with elegant accents
 * - Square corners on all images and cards
 * - Luxury typography (Cormorant Garamond + Montserrat)
 * - Editorial-style layouts
 * - Portfolio aesthetic throughout
 */
export const stoneInvestmentTheme: ThemeConfig = {
  id: 'stone-investment',
  name: 'Stone Investment',
  slug: 'stone-investment',
  category: 'Luxury Real Estate',
  description: 'Concept One — Cinematic luxury theme with video hero, HorizonOverlay cards, scroll-aware navigation, and editorial layouts inspired by high-fashion editorial design.',
  previewImage: '/themes/stone-investment/preview.jpg',
  
  defaultColors: {
    primary: '#1a1a1a',       // Charcoal - sophisticated dark
    secondary: '#0d0d0d',     // Near black - elegant elements
    accent: '#8a8a8a',        // Refined grey - subtle accent
    accentLight: '#c4c4c4',   // Light grey - secondary accent
    tertiary: '#2a2a2a',      // Dark grey - alternative shade
    background: '#fafafa',    // Off-white - clean background
    surface: '#ffffff',       // Pure white - card surfaces
    text: '#1a1a1a',          // Charcoal - primary text
    textMuted: '#6b6b6b',     // Medium grey - secondary text
    border: '#e5e5e5',        // Light grey - subtle borders
  },

  defaultFonts: {
    heading: "'Cormorant Garamond', 'Georgia', 'Times New Roman', serif",
    body: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  
  components: {
    // Type assertion to allow Stone Investment-specific components beyond core ThemeComponents
    Hero: StoneInvestmentHero,
    PropertyCard: StoneInvestmentPropertyCard,
    ServiceCard: StoneInvestmentServiceCard,
    ServicesSection: StoneInvestmentServicesSection,
    FeaturedPropertiesCarousel: StoneInvestmentFeaturedPropertiesCarousel,
    AgentsSection: StoneInvestmentAgentsSection,
    Navigation: StoneInvestmentNavigation,
    Footer: StoneInvestmentFooter,
    ContactForm: StoneInvestmentContactForm,
    LocaleSwitcher: StoneInvestmentLocaleSwitcher,
    StatsSection: StoneInvestmentStatsSection,
    CTASection: StoneInvestmentCTASection,
    // Post Components (Dynamic - Route Royal Documents)
    PostCard: StoneInvestmentPostCard,
    PostGrid: StoneInvestmentPostGrid,
    PostHero: StoneInvestmentPostHero,
    PostSearch: StoneInvestmentPostSearch,
    // Cast the filters component to match the core interface type
    PostFilters: StoneInvestmentPostFilters as unknown as ComponentType<PostFiltersProps>,
    PostArticle: StoneInvestmentPostArticle,
    PostPagination: StoneInvestmentPostPagination,
    // Legacy Blog Components (ThemeComponents compliance)
    BlogCard: StoneBlogCard,
    BlogHero: StoneBlogHero,
    BlogSearch: StoneBlogSearch,
    BlogFilters: StoneBlogFilters,
    BlogArticle: StoneBlogArticle,
    BlogGrid: StoneBlogGrid,
    // Page Components (Dynamic - Route Royal Static Pages)
    PageContent: StoneInvestmentPageContent,
    PageHeader: StoneInvestmentPageHeader,
    PageTableOfContents: StonePageTableOfContents,
    // Landing Page Sections (Stone Investment specific)
    IntroductionSection: StoneInvestmentIntroductionSection,
    SignatureSelection: StoneInvestmentSignatureSelection,
    FooterTeaser: StoneInvestmentFooterTeaser,
    // Dynamic Listing Components (API Integration)
    ListingCard: StoneListingCard,
    ListingGrid: StoneListingGrid,
    ListingsContent: StoneListingsContent,
    // Filter/Search Components
    FilterPanel: StoneFilterPanel,
    SearchBar: StoneSearchBar,
    SortControl: StoneSortControl,
    ViewSwitcher: StoneViewSwitcher,
    Pagination: StonePagination,
    AdvancedFiltersModal: StoneAdvancedFiltersModal,
    // Area Page Components
    AreaPage: StoneAreaPage,
    AreaHero: StoneAreaHero,
    AreaOverview: StoneAreaOverview,
    AreaStats: StoneAreaStats,
    AreaMap: StoneAreaMap,
    AreaProperties: StoneAreaProperties,
    AreaSelector: StoneAreaSelector,
    // Guide Page Components — imported directly in route files (server components
    // cannot be placed in the serialisable components map without triggering
    // "Functions cannot be passed directly to Client Components" errors).
    // GuidePage: StoneGuidePage,
    // GuidesHub: StoneGuidesHub,
    // Project Components
    // DevelopmentCard: StoneDevelopmentCard,
    // DevelopmentGrid: StoneDevelopmentGrid,
    ProjectCard: StoneProjectCard,
    ProjectDetailHero: StoneProjectDetailHero,
    ProjectDetailContent: StoneProjectDetailContent,
    ProjectHeroCarousel: StoneProjectHeroCarousel,
    ProjectShowcase: StoneProjectShowcase,
    ProjectsListView: StoneProjectsListView,
    // About Page Components
    PageHero: StonePageHero,
    CompanyStory: StoneCompanyStory,
    TeamSection: StoneTeamSection,
    ValuesSection: StoneValuesSection,
    LocationsGrid: StoneLocationsGrid,
    // Contact Components
    ContactFormSection: StoneContactFormSection,
    ContactMapHero: StoneContactMapHero,
    ContactSection: StoneContactSection,
    PhoneInput: StonePhoneInput,
    // Royale Spaces Integration
    SpacesCTA: StoneSpacesCTA,
    SpacesModal: StoneSpacesModal,
    SpacesWizard: StoneSpacesWizard,
    ImagineSpaceButton: StoneImagineSpaceButton,
    // Core Property Detail Wrappers
    PropertyOverview: StonePropertyOverview,
    PropertyGallery: StonePropertyGallery,
    PropertyFeatures: StonePropertyFeatures,
    PropertyAmenities: StonePropertyAmenities,
    PropertyContactCard: StonePropertyContactCard,
    PropertyLocation: StonePropertyLocation,
    // Contact Information Section
    ContactInformationSection: StoneContactInformationSection,
    // GEO FAQ
    FAQSection: StoneFAQSection,
    // Utility Components
    MapComponent: StoneMapComponent,
    ComingSoon: StoneComingSoon,
    PropertyMapView: StonePropertyMapView,
    PropertyListCard: StonePropertyListCard,
    // Note: JournalSection, ContactSection, TheFirmSection, ValuationsSection, MenuOverlay
    // are exported separately and used directly via imports, not through theme.components
  } as StoneInvestmentComponents,
  
  layoutConfig: {
    homepage: [
      { component: 'Hero', order: 1 },
      { 
        component: 'IntroductionSection', 
        order: 2,
        props: {
          title: 'A Tale of Two Worlds',
          description: 'We select homes that tell a story. Whether it is the warm breeze of the Indian Ocean or the scent of lavender in a Gordes courtyard, Stone Investment offers a portfolio of rare character and timeless beauty.'
        }
      },
      { 
        component: 'SignatureSelection',
        order: 3,
        props: {
          title: 'Signature Selection',
          eyebrow: 'Curated Highlights',
          maxItems: 3
        }
      },
      { 
        component: 'FooterTeaser',
        order: 4,
        props: {
          title: 'Selling Distinction',
          subtitle: 'Let us tell the story of your property',
          buttonText: 'Request a Private Valuation',
          buttonHref: '/contact?intent=valuation',
        }
      },
      { 
        component: 'FeaturedPropertiesCarousel',
        order: 5,
        props: {
          title: 'Our Selection',
          subtitle: 'A curated portfolio of exceptional properties in Mauritius and Provence',
          maxItems: 6
        }
      },
      // Buyer CTA moved earlier — immediately after the collection showcase
      { 
        component: 'CTASection', 
        order: 6,
        props: {
          title: 'Interested in Our Properties?',
          description: 'Contact us for a private consultation or to arrange a viewing of any property in our collection.',
          primaryButton: { text: 'Contact Us', href: '/contact?intent=consultation' },
          secondaryButton: { text: 'View Properties', href: '/properties' },
          background: 'primary'
        }
      },
      { 
        component: 'ServicesSection', 
        order: 7,
        props: {
          title: 'Our Expertise',
          subtitle: 'Comprehensive services for discerning property seekers',
          layout: 'bento',
          services: [
            {
              id: 'prestige',
              title: 'Prestige Property Sales',
              description: 'Exceptional properties including character houses, vineyards, sea front villas, and luxury apartments.'
            },
            {
              id: 'valuations',
              title: 'Property Valuations',
              description: 'Expert valuation services with deep knowledge of local markets and property characteristics.'
            },
            {
              id: 'mauritius',
              title: 'Mauritius Investment',
              description: 'Specialized expertise in Mauritius real estate including IRS, RES, PDS, R+2, and Smart City schemes with full legal and financial guidance.'
            },
            {
              id: 'architecture',
              title: 'Architectural Properties',
              description: 'Curated selection of iconic 20th century architectural movements and historically significant properties.'
            }
          ]
        }
      },
      {
        component: 'AgentsSection',
        order: 8,
        props: {
          title: 'Our Experts',
          subtitle: 'Bringing together expertise in luxury real estate across two continents',
          columns: 3,
          agents: [
            {
              id: 'pierre-monville',
              name: 'Pierre Monville',
              role: 'Founder & Managing Director',
              bio: 'For nearly twenty years, Pierre has specialized in real estate investments in Mauritius. He knows the different regions perfectly and masters the legal and financial frameworks. He also works on French property tax relief legislation.',
              email: 'p.monville@stoneinvestment.fr',
              phone: '+33 6 16 64 16 04',
              image: 'https://media.routeroyal.com/7q939t5U8kw4KYfV0-f1Z4Ie3jx_2Z_bxhJmMwd2YrA/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvZmVmMmU3ZWMzMGI0MjI3ODRjNjE4N2E2MTgzMDkwMjliMzM2YjlhYmY3ODllYWY5OTMyZjAyYzc0OThhYjZmYi5qcGc.jpg'
            },
            {
              id: 'noel-castinel',
              name: 'Noël Castinel',
              role: 'Provence',
              bio: 'Noël Castinel is the founder of VIP Real Estate in London. Bilingual in French and English, he has been selling exceptional properties in Provence, his native region, since 1992. He knows the best locations inside out. He now divides his time between London and Provence, where he leads a team of real estate enthusiasts.',
              email: 'n.castinel@stoneinvestment.fr',
              phone: '+33 6 12 56 46 20',
              image: 'https://media.routeroyal.com/dQlC868U6lS3Bq0IapSA8pI5UMj4Ri92rhUYH8yoHKw/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvZTEzMmMzY2EwNDgzZjg4NzY4YjllMTUxNGM0Y2E1NjcyODE4M2M3YzNjMjAyNWRiZjU3ZGQ1MzNmNjdlYzMxOS5qcGc.jpg'
            },
            {
              id: 'bertrand-meunier',
              name: 'Bertrand Meunier',
              role: 'Mauritius',
              bio: 'Bertrand has been leading the Mauritian office since 2014. He brings together fifteen years of experience in the publication of golf and tourism magazines, along with an equally extensive background in real estate across the Caribbean and Mauritius. Based on the west coast in Tamarin for nearly thirty years, he offers our clients a comprehensive understanding of Mauritius\' different regions and an unparalleled knowledge of the property market accessible to expatriates.',
              email: 'b.meunier@stoneinvestment.fr',
              phone: '+230 5 493 95 90',
              image: 'https://media.routeroyal.com/t5YRo1_Vuz4-4xonmUalT6kLE6pS3rsYE91pUTc26EQ/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvZjZiZGFhNDVkZjY3NGZlMjY5OWRmYWVjNTc5MTJmMzE4MDU2Y2ZlNWFiZmMzYTQ5ZmM1MDg3NmViM2RlMDhhMC5qcGc.jpg'
            }
          ]
        }
      },
      {
        component: 'FAQSection',
        order: 9,
        props: {
          source: 'home',
          className: 'bg-white',
        },
      },
    ],
  },
};

// Export all components
export { StoneInvestmentAgentsSection } from './components/AgentsSection';
export type { Agent, StoneInvestmentAgentsSectionProps } from './components/AgentsSection';

export { StoneInvestmentContactForm } from './components/ContactForm';

export { StoneInvestmentCTASection } from './components/CTASection';

export { StoneInvestmentFeaturedPropertiesCarousel } from './components/FeaturedPropertiesCarousel';
export type { StoneInvestmentFeaturedPropertiesCarouselProps } from './components/FeaturedPropertiesCarousel';

export { StoneInvestmentFooter } from './components/Footer';
export type { StoneInvestmentFooterProps } from './components/Footer';

export { StoneInvestmentHero } from './components/Hero';

// Hero Variants (Concept One - Five Distinct Styles)
export {
    // Base Hero Components
    AbstractHero,
    ArchitectHero,
    CinematicHero,
    EditorialHero,
    EssenceHero,
    // Pre-configured Page Heroes
    HomeHero,
    PropertiesHero,
    ShowcaseHero,
    TheFirmHero,
    ValuationsHero
} from './components/HeroVariants';
export type {
    AbstractHeroProps,
    ArchitectHeroProps,
    CinematicHeroProps,
    EditorialHeroProps,
    EssenceHeroProps,
    HeroBaseProps
} from './components/HeroVariants';

export { StoneInvestmentLocaleSwitcher } from './components/LocaleSwitcher';

export { StoneInvestmentCurrencySwitcher } from './components/CurrencySwitcher';

export { StoneInvestmentNavigation } from './components/Navigation';

export { StoneInvestmentMenuOverlay } from './components/MenuOverlay';
export type { default as MenuOverlayProps } from './components/MenuOverlay';

export { StoneInvestmentPageContent } from './components/PageContent';

export { StoneInvestmentPageHeader } from './components/PageHeader';

export { StoneInvestmentPostArticle } from './components/PostArticle';

export { StoneInvestmentPostCard } from './components/PostCard';

export { defaultPostFilters as stoneInvestmentDefaultPostFilters, StoneInvestmentPostFilters } from './components/PostFilters';
export type { StoneInvestmentPostFiltersProps, PostFiltersState as StoneInvestmentPostFiltersState } from './components/PostFilters';

export { StoneInvestmentPostGrid } from './components/PostGrid';

export { StoneInvestmentPostHero } from './components/PostHero';

export { StoneInvestmentPostPagination } from './components/PostPagination';

export { StoneInvestmentPostSearch } from './components/PostSearch';

export { HorizonOverlay, StoneInvestmentPropertyCard } from './components/PropertyCard';
export type { StoneInvestmentPropertyCardProps } from './components/PropertyCard';

export { StoneInvestmentServiceCard } from './components/ServiceCard';

export { StoneInvestmentServicesSection } from './components/ServicesSection';

export { StoneInvestmentStatsSection } from './components/StatsSection';

// Property Detail Page Components
export { StoneInvestmentPropertyContactCard } from './components/PropertyContactCard';
export type { StoneInvestmentPropertyContactCardProps } from './components/PropertyContactCard';

export { StoneInvestmentPropertyGallery } from './components/PropertyGallery';
export type { GalleryImage, StoneInvestmentPropertyGalleryProps } from './components/PropertyGallery';

export { StoneInvestmentPropertyHero } from './components/PropertyHero';
export type { StoneInvestmentPropertyHeroProps } from './components/PropertyHero';

export { StoneInvestmentPropertyOverview } from './components/PropertyOverview';
export type { StoneInvestmentPropertyOverviewProps } from './components/PropertyOverview';

// Region/Destination Components
export { StoneInvestmentRegionShowcase } from './components/RegionShowcase';
export type { Region, StoneInvestmentRegionShowcaseProps } from './components/RegionShowcase';

export { MauritiusPage, ProvencePage, RegionPage } from './components/RegionPage';
export type { RegionPageProps } from './components/RegionPage';

export { RegionBadge, RegionIndicator } from './components/RegionIndicator';
export type { RegionIndicatorProps } from './components/RegionIndicator';

// Concept One Components (New Design System)
export { StoneInvestmentTheFirmSection } from './components/TheFirmSection';
export type { StoneInvestmentTheFirmSectionProps } from './components/TheFirmSection';

export { StoneInvestmentValuationsSection } from './components/ValuationsSection';
export type { StoneInvestmentValuationsSectionProps } from './components/ValuationsSection';

export { StoneInvestmentJournalSection } from './components/JournalSection';
export type { Article, StoneInvestmentJournalSectionProps } from './components/JournalSection';

export { StoneInvestmentContactSection } from './components/ContactSection';
export type { StoneInvestmentContactSectionProps } from './components/ContactSection';

// Home Page Components (Concept One)
export { StoneInvestmentSignatureSelection } from './components/SignatureSelection';
export type { StoneInvestmentSignatureSelectionProps } from './components/SignatureSelection';

export { StoneInvestmentIntroductionSection } from './components/IntroductionSection';
export type { StoneInvestmentIntroductionSectionProps } from './components/IntroductionSection';

export { StoneInvestmentFooterTeaser } from './components/FooterTeaser';
export type { StoneInvestmentFooterTeaserProps } from './components/FooterTeaser';

// Region Utilities
export {
    detectRegion,
    getRegionColors,
    getRegionFlag, mauritiusColors, provenceColors, regionIcons, regionNames
} from './utils/regionColors';
export type { RegionColors, Region as RegionType } from './utils/regionColors';

// ============================================================================
// NEW DYNAMIC CAPABILITY COMPONENTS
// These components bring feature parity with the Allys theme while maintaining
// Stone Investment's distinctive luxury portfolio aesthetic
// ============================================================================

// Dynamic Listing Components (API Integration)
export { StoneListingCard } from './components/StoneListingCard';
export type { StoneListingCardProps } from './components/StoneListingCard';

export { StoneListingGrid, StoneListingGridSkeleton } from './components/StoneListingGrid';
export type { StoneListingGridProps } from './components/StoneListingGrid';

export { StoneListingsContent } from './components/StoneListingsContent';

// Filter/Search Components
export { StoneFilterPanel } from './components/StoneFilterPanel';
export type { FilterOption, FilterSection, StoneFilterPanelProps } from './components/StoneFilterPanel';

export { StoneSearchBar } from './components/StoneSearchBar';
export type { StoneSearchBarProps } from './components/StoneSearchBar';

export { StoneSortControl } from './components/StoneSortControl';
export type { SortOption, StoneSortControlProps } from './components/StoneSortControl';

export { StoneViewSwitcher } from './components/StoneViewSwitcher';
export type { StoneViewSwitcherProps, ViewMode } from './components/StoneViewSwitcher';

export { StonePagination } from './components/StonePagination';
export type { StonePaginationProps } from './components/StonePagination';

export { StoneAdvancedFiltersModal } from './components/StoneAdvancedFiltersModal';
export type { StoneAdvancedFiltersModalProps } from './components/StoneAdvancedFiltersModal';

// Area Index Component
export { StoneAreasIndex } from './components/StoneAreasIndex';
export type { StoneAreasIndexProps, StoneAreasIndexMessages } from './components/StoneAreasIndex';

// Area Page Components
export { StoneAreaPage } from './components/StoneAreaPage';
export type { StoneAreaPageProps } from './components/StoneAreaPage';

export { StoneAreaHero } from './components/StoneAreaHero';
export type { StoneAreaHeroProps } from './components/StoneAreaHero';

export { StoneAreaOverview } from './components/StoneAreaOverview';
export type { StoneAreaOverviewProps } from './components/StoneAreaOverview';

export { StoneAreaStats } from './components/StoneAreaStats';
export type { StoneAreaStatsProps } from './components/StoneAreaStats';

export { StoneAreaMap } from './components/StoneAreaMap';
export type { StoneAreaMapProps } from './components/StoneAreaMap';

export { StoneAreaProperties } from './components/StoneAreaProperties';
export type { StoneAreaPropertiesProps } from './components/StoneAreaProperties';

export { StoneAreaSelector } from './components/StoneAreaSelector';
export type { StoneAreaSelectorProps } from './components/StoneAreaSelector';

// Guide Page Components
export { StoneGuidePage } from './components/StoneGuidePage';
export type { StoneGuidePageProps } from './components/StoneGuidePage';

export { StoneGuidesHub } from './components/StoneGuidesHub';
export type { StoneGuidesHubProps } from './components/StoneGuidesHub';

export { StoneFAQ } from './components/StoneFAQ';
export type { StoneFAQItem, StoneFAQProps } from './components/StoneFAQ';

export { StoneFAQSection } from './components/StoneFAQSection';
export type { StoneFAQSectionProps, StoneFAQSource } from './components/StoneFAQSection';

// Project Components
export { StoneDevelopmentCard } from './components/StoneDevelopmentCard';
export type {
  DevelopmentCardData,
  ProjectStage,
  ProjectStatus,
  ProjectType,
  StoneDevelopmentCardProps
} from './components/StoneDevelopmentCard';

export { StoneDevelopmentGrid, StoneDevelopmentGridSkeleton } from './components/StoneDevelopmentGrid';
export type { StoneDevelopmentGridProps } from './components/StoneDevelopmentGrid';

export { StoneDevelopmentsListView } from './components/StoneDevelopmentsListView';
export type { StoneDevelopmentsListViewProps } from './components/StoneDevelopmentsListView';

export { StoneProjectCard } from './components/StoneProjectCard';
export type { StoneProjectCardProps } from './components/StoneProjectCard';

export { StoneProjectDetailHero } from './components/StoneProjectDetailHero';
export type { StoneProjectDetailHeroProps } from './components/StoneProjectDetailHero';

export { StoneProjectDetailContent } from './components/StoneProjectDetailContent';
export type { StoneProjectDetailContentProps } from './components/StoneProjectDetailContent';

export { StoneProjectHeroCarousel } from './components/StoneProjectHeroCarousel';
export type { StoneProjectHeroCarouselProps } from './components/StoneProjectHeroCarousel';

export { StoneProjectShowcase } from './components/StoneProjectShowcase';
export type { ProjectShowcaseItem, StoneProjectShowcaseProps } from './components/StoneProjectShowcase';

export { StoneProjectsListView } from './components/StoneProjectsListView';
export type { ProjectListItem, StoneProjectsListViewProps } from './components/StoneProjectsListView';

// About Page Components
export { StonePageHero } from './components/StonePageHero';

export { StoneCompanyStory } from './components/StoneCompanyStory';

export { StoneTeamSection } from './components/StoneTeamSection';

export { StoneValuesSection } from './components/StoneValuesSection';

export { StoneLocationsGrid } from './components/StoneLocationsGrid';

// Contact Components
export { StoneContactFormSection } from './components/StoneContactFormSection';
export type { ContactFormData, StoneContactFormSectionProps } from './components/StoneContactFormSection';

export { StoneContactMapHero } from './components/StoneContactMapHero';
export type { StoneContactMapHeroProps } from './components/StoneContactMapHero';

export { StoneContactSection } from './components/StoneContactSection';
export type { OfficeLocation, StoneContactSectionProps } from './components/StoneContactSection';

export { StonePhoneInput } from './components/StonePhoneInput';
export type { CountryCode, StonePhoneInputProps } from './components/StonePhoneInput';

export { StoneOfficeMap } from './components/StoneOfficeMap';
export type { OfficeMapLocation, StoneOfficeMapProps } from './components/StoneOfficeMap';

// Royale Spaces Integration
export { StoneSpacesCTA } from './components/StoneSpacesCTA';
export type { StoneSpacesCTAProps } from './components/StoneSpacesCTA';

export { StoneSpacesModal } from './components/StoneSpacesModal';
export type { StoneSpacesModalProps } from './components/StoneSpacesModal';

export { StoneSpacesWizard } from './components/StoneSpacesWizard';

export { StoneImagineSpaceButton } from './components/StoneImagineSpaceButton';
export type { StoneImagineSpaceButtonProps } from './components/StoneImagineSpaceButton';

// Cinematic Hero Carousel
export { StoneCinematicHeroCarousel } from './components/StoneCinematicHeroCarousel';
export type { CinematicHeroImage, StoneCinematicHeroCarouselProps } from './components/StoneCinematicHeroCarousel';

// Utility Components
export { AdaptiveLogo } from './components/AdaptiveLogo';
export type { AdaptiveLogoProps } from './components/AdaptiveLogo';

export { StoneMapComponent } from './components/StoneMapComponent';
export type { MapMarker, StoneMapComponentProps } from './components/StoneMapComponent';

export { StoneComingSoon } from './components/StoneComingSoon';
export type { StoneComingSoonProps } from './components/StoneComingSoon';

export { StonePageTableOfContents } from './components/StonePageTableOfContents';
export type { StonePageTableOfContentsProps, TocItem } from './components/StonePageTableOfContents';

export { StonePropertyMapView } from './components/StonePropertyMapView';
export type { StonePropertyMapViewProps } from './components/StonePropertyMapView';

export { StonePropertyListCard } from './components/StonePropertyListCard';
export type { StonePropertyListCardProps } from './components/StonePropertyListCard';

// Property Navigation Components (Concept One - Six Distinct Patterns)
export {
    PropertyDeck,
    PropertyIndex,
    PropertyJournal,
    PropertyPathfinder,
    PropertyReel,
    PropertySpotlight
} from './components/StonePropertyNavigation';
export type {
    PropertyDeckProps,
    PropertyIndexProps,
    PropertyJournalProps,
    PropertyNavigationItem,
    PropertyPathfinderProps,
    PropertyReelProps,
    PropertySpotlightProps
} from './components/StonePropertyNavigation';

// Property Card Styles (Concept One - Six Distinct Visual Styles)
export {
    CinemaCard,
    ClassicCard,
    DeckCard,
    EditorialCard,
    MinimalCard,
    SpotlightCard
} from './components/StonePropertyCardStyles';
export type {
    CinemaCardProps,
    ClassicCardProps,
    DeckCardProps,
    EditorialCardProps,
    MinimalCardProps,
    PropertyCardStyleProps,
    SpotlightCardProps
} from './components/StonePropertyCardStyles';

// Core Property Detail Wrappers
export { StonePropertyOverview } from './components/StonePropertyOverview';
export type { StonePropertyOverviewProps } from './components/StonePropertyOverview';

export { StonePropertyGallery } from './components/StonePropertyGallery';
export type { StonePropertyGalleryProps } from './components/StonePropertyGallery';

export { StonePropertyFeatures } from './components/StonePropertyFeatures';
export type { StonePropertyFeaturesProps } from './components/StonePropertyFeatures';

export { StonePropertyAmenities } from './components/StonePropertyAmenities';
export type { StonePropertyAmenitiesProps } from './components/StonePropertyAmenities';

export { StonePropertyContactCard } from './components/StonePropertyContactCard';

export { StonePropertyLocation } from './components/StonePropertyLocation';
export type { StonePropertyLocationProps } from './components/StonePropertyLocation';

// Contact Information Section
export { StoneContactInformationSection } from './components/StoneContactInformationSection';
export type { StoneContactInformationSectionProps } from './components/StoneContactInformationSection';

// Search Overlay
export { StoneSearchOverlay } from './components/StoneSearchOverlay';
export type { StoneSearchOverlayProps, SearchResultItem } from './components/StoneSearchOverlay';

// Legacy Blog Components
export { StoneBlogCard } from './components/StoneBlogCard';
export { StoneBlogHero } from './components/StoneBlogHero';
export { StoneBlogSearch } from './components/StoneBlogSearch';
export { StoneBlogFilters } from './components/StoneBlogFilters';
export { StoneBlogArticle } from './components/StoneBlogArticle';
export { StoneBlogGrid } from './components/StoneBlogGrid';

// Styles
// Import this CSS file in your app for full Concept One animations:
// import '@routeroyal/theme-stone-investment/dist/styles/concept-one.css';
